/**
 * DeliveryLocationPicker — change WHERE on campus you get deliveries.
 *
 * This is what the header's "Deliver to" opens. It deliberately does NOT offer
 * campuses: the college is fixed at sign-up, so the only thing left to change
 * is the hostel or block, which is also the thing students actually change.
 *
 * The choice is saved on the account rather than kept in the tab, so it
 * survives a reload, follows the student to another device, and prefills
 * checkout.
 */

import { useState } from 'react';
import { Modal, Spinner } from '@/components/ui';
import { DeliveryLocationList } from '@/components/DeliveryLocationList';
import { useCollege } from '@/context/CollegeContext';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { useDeliveryLocations } from '@/hooks/useStorefront';

export function DeliveryLocationPicker({ isOpen, onClose }) {
  const { collegeId, college } = useCollege();
  const { customer, updateProfile } = useCustomerAuth();
  const { data: locations = [], isLoading } = useDeliveryLocations(collegeId);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const currentId = customer?.defaultLocation?._id ?? customer?.defaultLocation ?? '';

  const choose = async (id) => {
    setError('');
    setSaving(id);
    try {
      await updateProfile({ defaultLocation: id });
      onClose();
    } catch (err) {
      setError(err?.message ?? 'Could not save that location. Please try again.');
    } finally {
      setSaving('');
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Where should we deliver?">
      {/* The campus is shown but not selectable — it is set for good. */}
      <p className="mb-4 rounded-lg bg-canvas px-3 py-2 text-xs text-muted">
        Delivering within{' '}
        <span className="font-semibold text-ink">{college?.name ?? 'your campus'}</span>
      </p>

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-danger">{error}</div>
      )}

      {isLoading ? (
        <div className="grid place-items-center py-10">
          <Spinner className="text-primary-600" />
        </div>
      ) : locations.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted">
          Your campus hasn&apos;t added any delivery points yet. You can still type an address at
          checkout.
        </p>
      ) : (
        <DeliveryLocationList
          locations={locations}
          selectedId={currentId}
          onSelect={choose}
          busyId={saving}
        />
      )}
    </Modal>
  );
}

export default DeliveryLocationPicker;
